import React, { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import type {
  EntityReconcileCandidate,
  EntityReconcileCreateResponse,
  EntityReconcileScoredResponse,
  EntityResponse,
} from 'types/api-responses';
import {
  ENTITY_TYPE_LABEL,
  type ReconcileCandidate,
} from 'pages/documents/structuring/reconcile/types';
import { actionErrorMessage } from 'utils/api-errors';
import {
  aliasGroupExtrasFor,
  consolidationCandidate,
  viewedEntityCandidate,
  scoredCandidate,
} from './reconcile-candidate-helpers';
import {
  entityReconcileCreateAtom,
  entityReconcileCreateUrl,
  entityReconcilePreviewAtom,
  entityReconcilePreviewUrl,
} from './atoms';
import ConsolidationTearsheet, {
  type ConsolidationTearsheetProps,
} from './consolidation-tearsheet';

interface Props {
  open: boolean;
  onClose: () => void;
  viewedEntity: EntityResponse;
  candidates: EntityReconcileCandidate[];
  // The viewed entity's own alias-family breakdown (its family is excluded from
  // `candidates`, so its right-panel Alias Group cell has no scored row to read).
  viewedAliasGroup: EntityReconcileScoredResponse['viewedAliasGroup'];
  moreFound: boolean;
  totalBeforeCap: number;
  sessionId: string;
  onComplete: (
    result: EntityReconcileCreateResponse,
    namesById: Record<number, string>
  ) => void;
  onCanonized?: () => void;
}

// Entity-page reconcile surface: builds the entity-specific config (viewed-entity
// anchor + its alias-family/participation extras, entity endpoints, canonical
// pre-check) and renders the shared ConsolidationTearsheet.
const EntityConsolidationTearsheet: React.FC<Props> = ({
  open,
  onClose,
  viewedEntity,
  candidates,
  viewedAliasGroup,
  moreFound,
  totalBeforeCap,
  sessionId,
  onComplete,
  onCanonized,
}) => {
  const aliasGroupExtras = useMemo(
    () =>
      aliasGroupExtrasFor(candidates, {
        id: viewedEntity.id,
        aliasGroup: viewedAliasGroup,
      }),
    [candidates, viewedAliasGroup, viewedEntity.id]
  );

  const toModel = useCallback(
    (c: ReconcileCandidate) => consolidationCandidate(c, aliasGroupExtras),
    [aliasGroupExtras]
  );

  // Anchor (the viewed entity) pinned first, then the scored suggestions.
  const originalCandidates = useMemo<ReconcileCandidate[]>(
    () => [
      viewedEntityCandidate(viewedEntity),
      ...candidates.map(scoredCandidate),
    ],
    [viewedEntity, candidates]
  );

  // §3.2 pre-check: a canonical viewed entity is pre-checked (its name the initial
  // survivor); an Imported viewed entity starts with nothing checked.
  const preCheckIds = useMemo(
    () => (viewedEntity.type !== 'ImportedEntity' ? [viewedEntity.id] : []),
    [viewedEntity]
  );

  const [previewState, triggerPreview] = useAtom(entityReconcilePreviewAtom);
  const [createState, triggerCreate] = useAtom(entityReconcileCreateAtom);

  // Entity-page preview honors the user's `groups` (Phase 2), so the previewed
  // fingerprints cover the actual selection.
  const submitPreview = useCallback<
    ConsolidationTearsheetProps['submitPreview']
  >(
    (ids, groups, onSuccess, onError) =>
      triggerPreview({
        url: entityReconcilePreviewUrl(viewedEntity.id),
        type: 'POST',
        data: { entity_ids: ids, groups },
        onSuccess: (data) => onSuccess(data),
        onError: (err) =>
          onError(actionErrorMessage(err, 'Could not preview reconciliation')),
      }),
    [triggerPreview, viewedEntity.id]
  );

  const submitCreate = useCallback<
    ConsolidationTearsheetProps<EntityReconcileCreateResponse>['submitCreate']
  >(
    (data, onSuccess, onError) =>
      triggerCreate({
        url: entityReconcileCreateUrl(viewedEntity.id),
        type: 'POST',
        data,
        onSuccess: (result) => onSuccess(result),
        onError: (err) => onError(err),
      }),
    [triggerCreate, viewedEntity.id]
  );

  return (
    <ConsolidationTearsheet<EntityReconcileCreateResponse>
      open={open}
      onClose={onClose}
      sessionId={sessionId}
      title={`Reconcile Entities — ${viewedEntity.name}`}
      originalCandidates={originalCandidates}
      toModel={toModel}
      preCheckIds={preCheckIds}
      moreFound={moreFound}
      totalBeforeCap={totalBeforeCap}
      shownCount={candidates.length}
      primaryEntityId={viewedEntity.id}
      primaryEntityType={ENTITY_TYPE_LABEL[viewedEntity.type]}
      previewLoading={previewState.loading}
      createLoading={createState.loading}
      submitPreview={submitPreview}
      submitCreate={submitCreate}
      onComplete={onComplete}
      onCanonized={onCanonized}
      showOperatorTag
    />
  );
};

export default EntityConsolidationTearsheet;
